import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { WMSStatCard } from '@/modules/wms/components/dashboard/WMSStatCard';
import { ScanInput } from '@/modules/wms/components/common/ScanInput';
import { rmaAPI, rtvAPI } from '@/shared/services/api';
import {
  Search,
  Plus,
  Package,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  ArrowRight,
  ShieldCheck,
  PackageX,
  Undo2,
  Loader2 } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';

// Add additional statuses for RMA
const rmaStatusConfig = {
  pending_inspection: { label: 'Pending Inspection', color: 'bg-amber-500' },
  pending: { label: 'Pending', color: 'bg-amber-500' },
  inspected: { label: 'Inspected', color: 'bg-blue-500' },
  restocked: { label: 'Restocked', color: 'bg-green-500' },
  completed: { label: 'Completed', color: 'bg-green-500' },
  quarantined: { label: 'Quarantined', color: 'bg-red-500' },
  disposed: { label: 'Disposed', color: 'bg-slate-500' }
};

// Return reasons for RMA creation
const mockReturnReasons = [
{ id: 'defective', label: 'Defective Product' },
{ id: 'wrong_item', label: 'Wrong Item Received' },
{ id: 'damaged', label: 'Damaged in Transit' },
{ id: 'not_as_described', label: 'Not as Described' },
{ id: 'changed_mind', label: 'Changed Mind' },
{ id: 'expired', label: 'Expired Product' },
{ id: 'other', label: 'Other' }];


export const Returns = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewRMADialog, setShowNewRMADialog] = useState(false);
  const [selectedRMA, setSelectedRMA] = useState(null);
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [rmaRequests, setRmaRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pendingInspection: 0, inspected: 0, restocked: 0, quarantined: 0, totalValue: 0 });

  // Fetch RMA data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rmaData, statsData] = await Promise.all([
        rmaAPI.getRMAs(),
        rmaAPI.getStats().catch(() => ({}))]
        );
        setRmaRequests(rmaData || []);
        const rmas = rmaData || [];
        setStats({
          pendingInspection: statsData?.pending || rmas.filter((r) => r.status === 'pending_inspection' || r.status === 'pending').length,
          inspected: statsData?.inspected || rmas.filter((r) => r.status === 'inspected').length,
          restocked: statsData?.restocked || rmas.filter((r) => r.status === 'restocked' || r.status === 'completed').length,
          quarantined: statsData?.quarantined || rmas.filter((r) => r.status === 'quarantined').length,
          totalValue: rmas.reduce((sum, r) => sum + (r.totalValue || r.total_value || 0), 0)
        });
      } catch (error) {
        console.error('Failed to fetch RMA data:', error);
        toast.error('Failed to load RMA requests');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleScan = (barcode) => {
    console.log('Scanned RMA:', barcode);
    // Would lookup RMA by barcode/order ID
  };

  const handleInspect = (rma) => {
    setSelectedRMA(rma);
    setShowInspectionDialog(true);
  };

  const getStatusBadge = (status) => {
    const config = rmaStatusConfig[status] || { label: status, color: 'bg-slate-500' };
    return (
      <Badge className={`${config.color} text-white border-0`}>
        {config.label}
      </Badge>);

  };

  return (
    <div className="space-y-6" data-testid="wms-returns">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("returnsRma", "Returns (RMA)")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("processIncomingReturns", "Process incoming returns, inspection, and restock/quarantine flow")}

          </p>
        </div>
        <Button
          className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
          onClick={() => setShowNewRMADialog(true)}>
          
          <Plus className="h-4 w-4" />{t("newRmaRequest", "New RMA Request")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <WMSStatCard
          title="Pending Inspection"
          value={stats.pendingInspection}
          icon={Clock}
          color="yellow"
          subtitle="Awaiting QA" />
        
        <WMSStatCard
          title="Inspected"
          value={stats.inspected}
          icon={ShieldCheck}
          color="blue"
          subtitle="Ready for decision" />
        
        <WMSStatCard
          title="Restocked"
          value={stats.restocked}
          icon={Undo2}
          color="green"
          subtitle="Returned to inventory" />
        
        <WMSStatCard
          title="Quarantined"
          value={stats.quarantined}
          icon={AlertTriangle}
          color="red"
          subtitle="Held for disposal" />
        
        <Card className="card-elevated">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t("totalValue", "Total Value")}

            </p>
            <p className="text-3xl font-bold mt-2 font-['Space_Grotesk']">
              ${(stats.totalValue ?? 0).toLocaleString()}
            </p>
            <p className="text-sm text-muted-foreground mt-1">{t("returnsThisMonth", "Returns this month")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Scan Input */}
      <Card className="card-elevated bg-gradient-to-r from-orange-50/50 to-transparent dark:from-orange-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/50">
              <RotateCcw className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scanReturn", "Scan Return")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanRmaBarcode", "Scan RMA barcode or enter order ID to process return")}</p>
            </div>
          </div>
          <ScanInput
            placeholder="Scan RMA barcode or enter order ID..."
            onScan={handleScan} />
          
        </CardContent>
      </Card>

      {/* RMA Workflow Visual */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("returnProcessingWorkflow", "Return Processing Workflow")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
            {[
            { step: 1, label: 'Receive', icon: Package, count: stats.pendingInspection },
            { step: 2, label: 'Inspect', icon: ShieldCheck, count: stats.inspected },
            { step: 3, label: 'Decision', icon: CheckCircle2, count: null },
            { step: 4, label: 'Restock', icon: Undo2, count: stats.restocked },
            { step: 5, label: 'Quarantine', icon: PackageX, count: stats.quarantined }].
            map((item, index) =>
            <React.Fragment key={item.step}>
                <div className="flex flex-col items-center min-w-[80px]">
                  <div className={`p-3 rounded-full ${
                item.count > 0 ? 'bg-blue-100 text-blue-600' : 'bg-muted text-muted-foreground'}`
                }>
                    <item.icon className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium mt-2">{item.label}</span>
                  {item.count !== null &&
                <Badge variant="secondary" className="mt-1 text-xs">
                      {item.count}
                    </Badge>
                }
                </div>
                {index < 4 &&
              <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              }
              </React.Fragment>
            )}
          </div>
        </CardContent>
      </Card>

      {/* RMA Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("rmaRequests", "RMA Requests")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by RMA ID or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("rmaId", "RMA ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("order", "Order")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("reason", "Reason")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={8} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingRmaRequests", "Loading RMA requests...")}</p>
                    </td>
                  </tr> :
                rmaRequests.map((rma) => {
                  const rmaId = rma.id || rma.rma_number;
                  const orderId = rma.orderId || rma.order_id || '-';
                  const customerName = rma.customer || rma.customer_name || '-';
                  const requestDate = rma.requestDate || rma.created_at || '-';
                  const returnReason = rma.returnReason || rma.reason || 'other';
                  const itemCount = rma.items?.length || rma.item_count || 0;
                  const totalValue = rma.totalValue || rma.total_value || 0;

                  return (
                    <tr key={rmaId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{rmaId}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm">{orderId}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-sm">{customerName}</p>
                          <p className="text-xs text-muted-foreground">{requestDate}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm capitalize">{returnReason.replace('_', ' ')}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-medium">{itemCount}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="font-medium">${totalValue.toFixed(2)}</span>
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(rma.status)}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" className="gap-1">
                            <Eye className="h-3.5 w-3.5" />{t("view", "View")}

                          </Button>
                          {(rma.status === 'pending_inspection' || rma.status === 'pending') &&
                          <Button size="sm" onClick={() => handleInspect(rma)}>{t("inspect", "Inspect")}

                          </Button>
                          }
                          {rma.status === 'inspected' &&
                          <Button size="sm" className="bg-green-600 hover:bg-green-700">{t("process", "Process")}

                          </Button>
                          }
                        </div>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* New RMA Dialog */}
      <Dialog open={showNewRMADialog} onOpenChange={setShowNewRMADialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createRmaRequest", "Create RMA Request")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t("orderId", "Order ID *")}</label>
              <Input placeholder="Enter original order ID" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("returnReason", "Return Reason *")}</label>
              <select className="w-full h-10 px-3 border border-input rounded-md bg-background">
                <option value="">{t("selectReason", "Select reason")}</option>
                {mockReturnReasons.map((r) =>
                <option key={r.id} value={r.id}>{r.label}</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("itemsToReturn", "Items to Return")}</label>
              <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t("scanOrSearch", "Scan or search items to add to return")}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t("notes", "Notes")}</label>
              <Input placeholder="Additional notes about the return" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNewRMADialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowNewRMADialog(false)}>{t("createRma", "Create RMA")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspection Dialog */}
      <Dialog open={showInspectionDialog} onOpenChange={setShowInspectionDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />{t("inspectReturn", "Inspect Return:")}
              {selectedRMA?.id}
            </DialogTitle>
          </DialogHeader>
          {selectedRMA &&
          <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">{t("customer", "Customer")}</p>
                  <p className="font-medium">{selectedRMA.customer}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("returnReason", "Return Reason")}</p>
                  <p className="font-medium capitalize">{selectedRMA.returnReason.replace('_', ' ')}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">{t("itemsToInspect", "Items to Inspect")}</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-2 px-3 text-sm font-medium">{t("sku", "SKU")}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium">{t("product", "Product")}</th>
                        <th className="text-center py-2 px-3 text-sm font-medium">{t("qty", "Qty")}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium">{t("condition", "Condition")}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium">{t("decision", "Decision")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRMA.items.map((item, idx) =>
                    <tr key={idx} className="border-b last:border-0">
                          <td className="py-2 px-3 font-mono text-sm">{item.sku}</td>
                          <td className="py-2 px-3 text-sm">{item.name}</td>
                          <td className="py-2 px-3 text-center">{item.qty}</td>
                          <td className="py-2 px-3">
                            <select className="h-8 px-2 border border-input rounded text-sm bg-background">
                              <option value="good">{t("good", "Good")}</option>
                              <option value="damaged">{t("damaged", "Damaged")}</option>
                              <option value="expired">{t("expired", "Expired")}</option>
                            </select>
                          </td>
                          <td className="py-2 px-3">
                            <select className="h-8 px-2 border border-input rounded text-sm bg-background">
                              <option value="restock">{t("restock", "Restock")}</option>
                              <option value="quarantine">{t("quarantine", "Quarantine")}</option>
                              <option value="dispose">{t("dispose", "Dispose")}</option>
                            </select>
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">{t("inspectionNotes", "Inspection Notes")}</label>
                <Input placeholder="Add inspection notes..." />
              </div>
            </div>
          }
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowInspectionDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => setShowInspectionDialog(false)}>{t("completeInspection", "Complete Inspection")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};